import { Link } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { useAfterInitialLoad } from "@/lib/deferredAssets";
import { cfImage } from "@/lib/utils";

/**
 * Footer component matching the old longitude-elite design
 */
export const Footer = () => {
  const { tenant, tenantId } = useTenant();
  const isWvu = tenantId === "wvu";
  const isFsu = tenantId === "fsu";
  const isUnisun = tenantId === "unisun";
  const brandAssetsReady = useAfterInitialLoad();

  const bgClass =
    tenant.layout?.footer?.backgroundClass ??
    "w-full bg-primary py-8 px-4 md:px-16 grid grid-cols-1 md:grid-cols-3 items-center gap-4";

  return (
    <footer id="site-footer" className={bgClass}>
      {/* Logo */}
      <div className="flex justify-center md:justify-start">
        {tenant.branding?.logos?.footer ? (
          brandAssetsReady ? (
            <img
              src={cfImage(tenant.branding.logos.footer, { width: isWvu ? 150 : 280, quality: 90 })}
              alt={`${tenant.branding.siteTitle} wordmark`}
              width={isWvu ? 150 : 280}
              height={isWvu ? 72 : 56}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              className={isWvu ? "" : "h-14 w-auto object-contain"}
            />
          ) : (
            <span
              aria-hidden="true"
              className={isWvu ? "block h-[72px] w-[150px]" : "block h-14 w-[280px]"}
            />
          )
        ) : (
          <span className="text-white text-lg font-bold">
            {tenant.branding?.siteTitle}
          </span>
        )}
      </div>

      {/* Links */}
      <div className={`flex flex-col md:flex-row items-center gap-2 md:gap-6 justify-center text-white ${isWvu ? "mt-4 md:mt-0" : ""}`}>
        <Link to="/support" className="hover:text-gray-200 transition-colors">
          Support
        </Link>
        <Link to="/terms-of-service" className="hover:text-gray-200 transition-colors">
          Terms & Conditions
        </Link>
        <Link to="/privacy-policy" className="hover:text-gray-200 transition-colors">
          Privacy Policy
        </Link>
      </div>

      {/* Small Logo */}
      <div className={`flex justify-center md:justify-end ${isWvu ? "mt-4 md:mt-0" : ""}`}>
        {tenant.branding?.logos?.small && !isFsu && !isUnisun && (
          isWvu ? (
            brandAssetsReady ? (
              <img
                src={cfImage(tenant.branding.logos.small, { width: 100, quality: 90 })}
                alt={`${tenant.branding.siteTitle} mark`}
                width={100}
                height={100}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                className="hidden md:block"
              />
            ) : (
              <span aria-hidden="true" className="hidden h-[100px] w-[100px] md:block" />
            )
          ) : (
            brandAssetsReady ? (
              <>
                <img
                  src={cfImage(tenant.branding.logos.small, { width: 112, quality: 90 })}
                  alt={`${tenant.branding.siteTitle} mark`}
                  width={56}
                  height={56}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className="h-14 w-auto hidden md:block"
                />
                <img
                  src={cfImage(tenant.branding.logos.small, { width: 96, quality: 90 })}
                  alt={`${tenant.branding.siteTitle} mark`}
                  width={48}
                  height={48}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className="h-12 w-auto block md:hidden"
                />
              </>
            ) : (
              <>
                <span aria-hidden="true" className="hidden h-14 w-14 md:block" />
                <span aria-hidden="true" className="block h-12 w-12 md:hidden" />
              </>
            )
          )
        )}
      </div>
    </footer>
  );
};

export default Footer;
